/**
 * Undo / redo.
 *
 * A bounded snapshot stack. Documents are small (a play is a few kB) and every
 * operation already returns a fresh immutable value, so snapshotting is both
 * cheap and exactly correct — no command inversion to get subtly wrong.
 */

export interface History<T> {
  readonly past: readonly T[];
  readonly present: T;
  readonly future: readonly T[];
  readonly limit: number;
  /**
   * The state a run of `replace`s started from. The next `push` records its
   * undo step from here, so a drag undoes to where the piece was picked up
   * rather than to its last previewed frame.
   */
  readonly before?: T;
}

export function createHistory<T>(present: T, limit = 100): History<T> {
  return { past: [], present, future: [], limit };
}

/** Records a new state. Clears the redo stack, as every editor does. */
export function push<T>(history: History<T>, present: T): History<T> {
  const previous = history.before ?? history.present;
  if (present === previous) return history.before === undefined ? history : { ...history, present, before: undefined };
  const past = [...history.past, previous];
  return {
    ...history,
    past: past.length > history.limit ? past.slice(past.length - history.limit) : past,
    present,
    future: [],
    before: undefined,
  };
}

/** Replaces the present without creating an undo step — for drag-in-progress. */
export function replace<T>(history: History<T>, present: T): History<T> {
  return { ...history, present, before: history.before ?? history.present };
}

/**
 * Rewrites the present with no undo step of its own, for bookkeeping that is
 * not an edit — e.g. linking the board to the play it was just saved as. The
 * next edit's undo step starts from the rewritten state.
 */
export function amend<T>(history: History<T>, change: (present: T) => T): History<T> {
  return { ...history, present: change(history.present) };
}

export function canUndo<T>(history: History<T>): boolean {
  return history.past.length > 0;
}

export function canRedo<T>(history: History<T>): boolean {
  return history.future.length > 0;
}

/** Drops an in-flight preview, so undo and redo move between committed states only. */
function settled<T>(history: History<T>): History<T> {
  return history.before === undefined ? history : { ...history, present: history.before, before: undefined };
}

export function undo<T>(history: History<T>): History<T> {
  const committed = settled(history);
  const previous = committed.past[committed.past.length - 1];
  if (previous === undefined) return committed;
  return {
    ...committed,
    past: committed.past.slice(0, -1),
    present: previous,
    future: [committed.present, ...committed.future],
  };
}

export function redo<T>(history: History<T>): History<T> {
  const committed = settled(history);
  const next = committed.future[0];
  if (next === undefined) return committed;
  return {
    ...committed,
    past: [...committed.past, committed.present],
    present: next,
    future: committed.future.slice(1),
  };
}
